import React, { useState, useRef } from 'react';
import { auth } from '../../lib/firebase';
import { getUserProfile } from '../../services/auth';
import { uploadListingImage } from '../../services/storage';
import { createBulkListings, type BulkDraft } from '../../services/listings';
import { toUserMessage } from '../../utils/errors';

interface BulkListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (ids: string[]) => void;
}

type UploadStatus = 'uploading' | 'done' | 'error';

interface DraftItem {
  key: string;
  file: File;
  previewUrl: string;
  imageUrl?: string;
  status: UploadStatus;
  title: string;
  price: string;
  category: string;
  condition: string;
  description: string;
}

const CATEGORY_OPTIONS = ['textbooks', 'electronics', 'fashion', 'furniture', 'sports', 'others'];
const CONDITION_OPTIONS = ['new', 'used - like new', 'used - good', 'used - acceptable'];

let keySeq = 0;

const BulkListingModal: React.FC<BulkListingModalProps> = ({ isOpen, onClose, onCreated }) => {
  const [items, setItems] = useState<DraftItem[]>([]);
  const [sharedCategory, setSharedCategory] = useState('');
  const [sharedCondition, setSharedCondition] = useState('');
  const [sharedLocation, setSharedLocation] = useState('');
  const [asGroup, setAsGroup] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const patch = (key: string, next: Partial<DraftItem>) =>
    setItems(prev => prev.map(it => (it.key === key ? { ...it, ...next } : it)));

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const user = auth.currentUser;
    if (!user) { setError('Please log in to post listings.'); return; }
    setError(null);

    const files = Array.from(fileList);
    const newItems: DraftItem[] = files.map((file) => ({
      key: `d${keySeq++}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading',
      title: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 60),
      price: '',
      category: '',
      condition: '',
      description: '',
    }));
    setItems(prev => [...prev, ...newItems]);

    // Upload each in the background so publishing later is instant.
    newItems.forEach(async (it) => {
      try {
        const url = await uploadListingImage(it.file, user.uid);
        patch(it.key, { imageUrl: url, status: 'done' });
      } catch {
        patch(it.key, { status: 'error' });
      }
    });
  };

  const removeItem = (key: string) => {
    setItems(prev => {
      const target = prev.find(i => i.key === key);
      if (target) { try { URL.revokeObjectURL(target.previewUrl); } catch {} }
      return prev.filter(i => i.key !== key);
    });
  };

  const retryUpload = (key: string) => {
    const user = auth.currentUser;
    const it = items.find(i => i.key === key);
    if (!user || !it) return;
    patch(key, { status: 'uploading' });
    uploadListingImage(it.file, user.uid)
      .then(url => patch(key, { imageUrl: url, status: 'done' }))
      .catch(() => patch(key, { status: 'error' }));
  };

  const uploadingCount = items.filter(i => i.status === 'uploading').length;
  const readyItems = items.filter(i => i.status === 'done' && i.imageUrl);
  const publishable = readyItems.filter(i => i.title.trim() && Number(i.price) > 0);
  const needsAttention = readyItems.filter(i => !i.title.trim() || !(Number(i.price) > 0));

  const handlePublish = async () => {
    const user = auth.currentUser;
    if (!user) { setError('Please log in.'); return; }
    if (publishable.length === 0) {
      setError('Add a title and price to at least one uploaded item.');
      return;
    }
    if (asGroup && !groupTitle.trim()) {
      setError('Give your group a title (e.g. "Room clear-out", "Ankara collection").');
      return;
    }
    setError(null);
    setPublishing(true);
    setProgress(0);
    try {
      let university: string | undefined;
      try { university = ((await getUserProfile(user.uid)) as any)?.university || undefined; } catch {}

      const drafts: BulkDraft[] = publishable.map(i => ({
        imageUrl: i.imageUrl!,
        title: i.title.trim(),
        price: Number(i.price),
        category: i.category || undefined,
        condition: i.condition || undefined,
        description: i.description.trim() || undefined,
      }));

      const groupId = asGroup ? `grp_${Date.now()}_${user.uid.slice(0, 6)}` : undefined;
      setProgress(40);
      const ids = await createBulkListings(drafts, user, {
        category: sharedCategory || undefined,
        condition: sharedCondition || undefined,
        location: sharedLocation || undefined,
        university,
        groupId,
        groupTitle: asGroup ? groupTitle.trim() : undefined,
      });
      setProgress(100);

      items.forEach(i => { try { URL.revokeObjectURL(i.previewUrl); } catch {} });
      setItems([]);
      setGroupTitle('');
      setAsGroup(false);
      onCreated?.(ids);
      onClose();
    } catch (e: any) {
      setError(toUserMessage(e) || 'Failed to publish listings.');
    } finally {
      setPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bulk Upload</h3>
            <p className="text-xs text-gray-500">Drop your photos, then add a name &amp; price to each. Publish them all at once.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Dropzone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 py-8 text-center hover:border-teal-400 hover:bg-teal-50/40 transition-colors"
          >
            <i className="ri-upload-cloud-2-line text-4xl text-teal-500"></i>
            <p className="mt-2 text-sm font-medium text-gray-700">Click to upload or drag &amp; drop multiple photos</p>
            <p className="text-xs text-gray-500">PNG, JPG, WEBP — as many as you like</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); if (e.target) e.target.value = ''; }}
            />
          </div>

          {items.length > 0 && (
            <>
              {/* Apply-to-all bar */}
              <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-100">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Apply to all (optional)</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <select value={sharedCategory} onChange={(e) => setSharedCategory(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm capitalize">
                    <option value="">Category…</option>
                    {CATEGORY_OPTIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                  <select value={sharedCondition} onChange={(e) => setSharedCondition(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm capitalize">
                    <option value="">Condition…</option>
                    {CONDITION_OPTIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                  <input value={sharedLocation} onChange={(e) => setSharedLocation(e.target.value)} placeholder="Location (e.g. FUTA North Gate)" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <p className="mt-1.5 text-[11px] text-gray-400">These fill in for any item you leave blank.</p>
              </div>

              {/* Group toggle */}
              <div className="rounded-xl bg-teal-50/60 p-3 ring-1 ring-teal-100">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <input type="checkbox" checked={asGroup} onChange={(e) => setAsGroup(e.target.checked)} className="h-4 w-4 rounded text-teal-600" />
                  Publish as a group / collection
                </label>
                {asGroup && (
                  <input
                    value={groupTitle}
                    onChange={(e) => setGroupTitle(e.target.value)}
                    placeholder="Group title — e.g. “Hostel move-out sale”"
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                )}
                <p className="mt-1 text-[11px] text-teal-700/80">Buyers see grouped items stacked together on the marketplace, like an album.</p>
              </div>

              {/* Item cards */}
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={it.key} className="flex gap-3 rounded-xl border border-gray-200 p-3">
                    <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      <img src={it.previewUrl} alt="" className="h-full w-full object-cover" />
                      {it.status === 'uploading' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <i className="ri-loader-4-line animate-spin text-xl text-white"></i>
                        </div>
                      )}
                      {it.status === 'error' && (
                        <button onClick={() => retryUpload(it.key)} title="Retry upload" className="absolute inset-0 flex flex-col items-center justify-center bg-red-600/70 text-white">
                          <i className="ri-refresh-line text-lg"></i>
                          <span className="text-[9px]">Retry</span>
                        </button>
                      )}
                      {it.status === 'done' && (
                        <span className="absolute bottom-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-white">
                          <i className="ri-check-line text-[10px]"></i>
                        </span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1 space-y-2">
                      <input
                        value={it.title}
                        onChange={(e) => patch(it.key, { title: e.target.value })}
                        placeholder="Item name"
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                      />
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <div className="relative">
                          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">₦</span>
                          <input
                            type="number"
                            value={it.price}
                            onChange={(e) => patch(it.key, { price: e.target.value })}
                            placeholder="Price"
                            className="w-full rounded-lg border border-gray-300 py-1.5 pl-6 pr-2 text-sm"
                          />
                        </div>
                        <select value={it.category} onChange={(e) => patch(it.key, { category: e.target.value })} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm capitalize">
                          <option value="">{sharedCategory ? `${sharedCategory} (shared)` : 'Category…'}</option>
                          {CATEGORY_OPTIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                        </select>
                        <select value={it.condition} onChange={(e) => patch(it.key, { condition: e.target.value })} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm capitalize">
                          <option value="">{sharedCondition ? `${sharedCondition} (shared)` : 'Condition…'}</option>
                          {CONDITION_OPTIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                        </select>
                      </div>
                      <textarea
                        value={it.description}
                        onChange={(e) => patch(it.key, { description: e.target.value })}
                        placeholder="Description (optional)"
                        rows={2}
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                      />
                    </div>

                    <button onClick={() => removeItem(it.key)} className="flex-shrink-0 self-start text-gray-400 hover:text-red-500" aria-label="Remove">
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          {publishing && (
            <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full bg-teal-600 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              {items.length === 0 ? 'No photos yet' : (
                <>
                  <span className="font-semibold text-gray-700">{publishable.length}</span> ready
                  {uploadingCount > 0 && <> · {uploadingCount} uploading…</>}
                  {needsAttention.length > 0 && <> · <span className="text-orange-600">{needsAttention.length} need name/price</span></>}
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                onClick={handlePublish}
                disabled={publishing || publishable.length === 0}
                className="rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {publishing ? 'Publishing…' : `Publish ${publishable.length || ''} item${publishable.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkListingModal;
